import { useState, useCallback, useRef, useEffect } from 'react';
import imageCompression from 'browser-image-compression';
import { bytesToKb } from '../../../@iosper/utils/Format';

const imageConfigCompression = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1024,
  useWebWorker: true,
};

/**
 * Hook personalizado para manejar la lógica del componente FileUploadBox
 * Encapsula drag & drop, validaciones, gestión de estado UI y compresión de archivos
 *
 * @param {Object} params - Parámetros del hook
 * @param {Array<File>} params.files - Archivos actuales
 * @param {Function} params.onFilesChange - Callback cuando cambian los archivos (recibe archivos comprimidos)
 * @param {number} params.maxFiles - Máximo de archivos permitidos
 * @param {number} params.maxSizeMB - Tamaño máximo total en MB
 * @param {boolean} params.disabled - Si está deshabilitado
 * @param {boolean} params.compressFiles - Si se deben comprimir los archivos (default: true)
 * @param {Function} params.setMessage - Función para mostrar mensajes al usuario
 * @returns {Object} Estado y métodos del hook
 */
const useFileUploadBox = ({
  files = [],
  onFilesChange,
  maxFiles = 3,
  maxSizeMB = 4,
  disabled = false,
  compressFiles = true,
  setMessage,
}) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(null);
  const [filesToCompress, setFilesToCompress] = useState([]);

  /**
   * Effect para procesar archivos a comprimir
   */
  useEffect(() => {
    if (filesToCompress.length > 0) {
      compressFilesSequentially(filesToCompress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesToCompress]);

  /**
   * Determina si un archivo es comprimible (imagen)
   * @param {File} file - Archivo a validar
   * @returns {boolean}
   */
  const isCompressibleFile = useCallback((file) => {
    return file.type?.startsWith('image/') || false;
  }, []);

  /**
   * Comprime un archivo de imagen
   * @param {File} file - Archivo a comprimir
   * @returns {Promise<File>}
   */
  const compressImageFile = useCallback(async (file) => {
    try {
      const options = {
        ...imageConfigCompression,
        onProgress: (p) => {
          setCompressionProgress(p);
        },
      };

      if (bytesToKb(file.size) <= 600) {
        return file;
      }

      // browser-image-compression siempre retorna Blob, nunca File
      const compressedBlob = await imageCompression(file, options);

      // Convertir Blob -> File preservando metadatos del original
      const mimeType = compressedBlob.type || file.type;
      const compressedFile = new File([compressedBlob], file.name, {
        type: mimeType,
        lastModified: file.lastModified || Date.now(),
      });

      return compressedFile;
    } catch (error) {
      console.error('Error al comprimir archivo:', error);
      return undefined; // Retornar archivo original si hay error
    }
  }, []);

  /**
   * Comprime archivos secuencialmente y los agrega a la lista
   * @param {Array<File>} filesToProcess - Archivos a procesar
   */
  const compressFilesSequentially = useCallback(
    async (filesToProcess) => {
      setIsCompressing(true);
      const compressedFiles = [];

      try {
        for (let i = 0; i < filesToProcess.length; i++) {
          const file = filesToProcess[i];
          let processedFile = file;

          if (compressFiles && isCompressibleFile(file)) {
            processedFile = await compressImageFile(file);
          }

          compressedFiles.push(processedFile);
          setCompressionProgress(((i + 1) / filesToProcess.length) * 100);
        }

        // Agregar archivos comprimidos a la lista
        onFilesChange([...files, ...compressedFiles]);
        setFilesToCompress([]);
        setCompressionProgress(null);
      } catch (error) {
        console.error('Error durante la compresión:', error);
        setMessage({
          open: true,
          message: 'Error al procesar los archivos',
          severity: 'error',
        });
        setFilesToCompress([]);
      } finally {
        setIsCompressing(false);
        setCompressionProgress(null);
      }
    },
    [files, compressFiles, isCompressibleFile, compressImageFile, onFilesChange],
  );

  /**
   * Valida si un archivo ya existe en la lista
   * Compara nombre + tipo + tamaño + fecha de modificación para evitar
   * falsos positivos con fotos de cámara que tienen nombres genéricos (ej: image.jpg)
   * @param {File} newFile - Archivo a validar
   * @returns {boolean} true si el archivo ya existe
   */
  const isDuplicateFile = useCallback(
    (newFile) => {
      return files.some((existingFileWrapper) => {
        const existingFile = existingFileWrapper.file || existingFileWrapper;
        const fileName = existingFile.fileName || existingFile.name;
        const fileType = existingFile.type || existingFile.file?.type;
        const fileSize = existingFileWrapper.size || existingFile.size || 0;
        const fileLastModified = existingFile.lastModified || 0;

        return (
          fileName === newFile.name &&
          fileType === newFile.type &&
          fileSize === newFile.size &&
          fileLastModified === newFile.lastModified
        );
      });
    },
    [files],
  );

  /**
   * Obtiene el icono apropiado según el tipo de archivo
   * @param {File} file - Archivo
   * @returns {string} Tipo de icono ('pdf', 'image', 'file')
   */
  const getFileIconType = useCallback((file) => {
    if (file.type === 'application/pdf') return 'pdf';
    if (file.type?.startsWith('image/')) return 'image';
    return 'file';
  }, []);

  /**
   * Formatea el tamaño del archivo en formato legible
   * @param {number} bytes - Tamaño en bytes
   * @returns {string} Tamaño formateado
   */
  const formatFileSize = useCallback((bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }, []);

  /**
   * Calcula el tamaño total de archivos
   * @returns {number} Tamaño total en bytes
   */
  const getTotalSize = useCallback(() => {
    return files.reduce((sum, fileMetadata) => {
      // Manejar tanto objetos File como objetos fileMetadata con propiedad file
      const file = fileMetadata.file || fileMetadata;
      // Para archivos existing/pending, usar fileMetadata.size directamente
      const size = fileMetadata.size || file.size || 0;
      return sum + size;
    }, 0);
  }, [files]);

  /**
   * Calcula el tamaño total en MB
   * @returns {number} Tamaño en MB
   */
  const getTotalSizeMB = useCallback(() => {
    return getTotalSize() / (1024 * 1024);
  }, [getTotalSize]);

  /**
   * Verifica si se alcanzó el máximo de archivos
   * @returns {boolean}
   */
  const isMaxFiles = useCallback(() => {
    return files.length >= maxFiles;
  }, [files.length, maxFiles]);

  /**
   * Valida y filtra archivos antes de agregarlos
   * @param {FileList|Array} newFiles - Nuevos archivos
   * @returns {Array<File>} Archivos válidos para agregar
   */
  const validateAndFilterFiles = useCallback(
    (newFiles) => {
      if (disabled) return [];

      const fileArray = Array.from(newFiles);
      const validFiles = [];

      // Verificar duplicados
      for (const file of fileArray) {
        if (isDuplicateFile(file)) {
          setMessage({
            open: true,
            message: `El archivo "${file.name}" ya fue agregado`,
            severity: 'error',
          });
          return [];
        }
      }

      // Verificar cantidad máxima
      const remainingSlots = maxFiles - files.length;
      if (fileArray.length > remainingSlots) {
        setMessage({
          open: true,
          message: `Solo puede agregar ${remainingSlots} archivo(s) más. Máximo: ${maxFiles}`,
          severity: 'error',
        });
        return validFiles.slice(0, remainingSlots);
      }

      // Todos los archivos son válidos
      return fileArray.slice(0, remainingSlots);
    },
    [disabled, files.length, maxFiles, isDuplicateFile],
  );

  /**
   * Maneja la selección de archivos
   * @param {FileList|Array} newFiles - Archivos seleccionados
   */
  const handleFileSelect = useCallback(
    (newFiles) => {
      const validFiles = validateAndFilterFiles(newFiles);

      if (validFiles.length > 0) {
        // Iniciar proceso de compresión
        setFilesToCompress(validFiles);
      }
    },
    [validateAndFilterFiles],
  );

  /**
   * Maneja el click en el área de upload
   */
  const handleButtonClick = useCallback(() => {
    if (!disabled && !isMaxFiles() && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled, isMaxFiles]);

  /**
   * Maneja el cambio en el input de archivos
   * @param {Event} event - Evento de cambio
   */
  const handleInputChange = useCallback(
    (event) => {
      const selectedFiles = event.target.files;
      if (selectedFiles && selectedFiles.length > 0) {
        handleFileSelect(selectedFiles);
      }
      // Resetear input para permitir seleccionar el mismo archivo después de eliminarlo
      event.target.value = '';
    },
    [handleFileSelect],
  );

  /**
   * Maneja la eliminación de un archivo
   * @param {number} index - Índice del archivo a eliminar
   */
  const handleRemoveFile = useCallback(
    (index) => {
      if (disabled) return;

      const newFiles = files.filter((_, i) => i !== index);
      onFilesChange(newFiles);
    },
    [disabled, files, onFilesChange],
  );

  /**
   * Maneja eventos de drag & drop
   */
  const handleDragEnter = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !isMaxFiles()) {
        setIsDragging(true);
      }
    },
    [disabled, isMaxFiles],
  );

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (!disabled && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFileSelect(e.dataTransfer.files);
      }
    },
    [disabled, handleFileSelect],
  );

  /**
   * Limpia el mensaje de error
   */
  const clearError = useCallback(() => {
    setErrorMessage('');
  }, []);

  return {
    // Refs
    fileInputRef,

    // Estado
    isDragging,
    errorMessage,
    isCompressing,
    compressionProgress,

    // Métodos de utilidad
    getFileIconType,
    formatFileSize,
    getTotalSize,
    getTotalSizeMB,
    isMaxFiles: isMaxFiles(),

    // Handlers
    handleButtonClick,
    handleInputChange,
    handleRemoveFile,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    clearError,
  };
};

export default useFileUploadBox;
